import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader2, Paperclip, X, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * EmailComposer - Modal for composing and sending emails
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onOpenChange - Callback when modal open state changes
 * @param {string} type - Type of email: "reiniging_offerte", "reiniging_factuur", "installatie_offerte", etc.
 * @param {object} data - Data to populate the email (customer, offerte/factuur details)
 * @param {string} pdfBase64 - Optional base64 encoded PDF attachment
 * @param {string} pdfName - Name for the PDF attachment
 */
export const EmailComposer = ({
  open,
  onOpenChange,
  type,
  data = {},
  pdfBase64,
  pdfName,
  onEmailSent
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    body_html: ''
  });

  // Fetch templates on open
  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  // Pre-fill form when data changes
  useEffect(() => {
    if (data) {
      setEmailForm(prev => ({
        ...prev,
        to_email: data.customer_email || '',
        to_name: data.customer_name || ''
      }));
    }
  }, [data]);

  // Apply template when selected
  useEffect(() => {
    if (selectedTemplate && data) {
      applyTemplate(selectedTemplate);
    }
  }, [selectedTemplate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
      
      // Auto-select matching template based on type
      const matchingTemplate = res.data.find(t => t.template_type === type && t.is_active);
      if (matchingTemplate) {
        setSelectedTemplate(matchingTemplate);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Kon email templates niet laden');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    // Build variables from data
    const variables = {
      klant_naam: data.customer_name || '',
      klant_email: data.customer_email || '',
      adres: data.address || '',
      offerte_nummer: data.offerte_nummer || data.reference_number || '',
      factuur_nummer: data.factuur_nummer || data.reference_number || '',
      totaal_bedrag: data.total_amount ? data.total_amount.toFixed(2) : '0.00',
      aantal_panelen: data.panel_count || '',
      geldigheid_dagen: data.validity_days || '14',
      betalingstermijn: data.payment_terms_days || '14',
      bedrijf_naam: data.company_name || 'Business Assistant',
      datum: data.date || new Date().toLocaleDateString('nl-NL'),
      tijd: data.time || '',
      afspraak_type: data.appointment_type || '',
      producten_lijst: buildProductsList(data.products || [])
    };

    // Replace variables in subject and body
    let subject = template.subject;
    let body = template.body_html;

    Object.entries(variables).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      subject = subject.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      body = body.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    setEmailForm(prev => ({
      ...prev,
      subject,
      body_html: body
    }));
  };

  const buildProductsList = (products) => {
    if (!products || products.length === 0) return '';
    
    let html = '<ul>';
    products.forEach(product => {
      html += `<li>${product.quantity || 1}x ${product.name}${product.brand ? ` (${product.brand})` : ''}</li>`;
    });
    html += '</ul>';
    return html;
  };

  const handleSend = async () => {
    if (!emailForm.to_email) {
      toast.error('Voer een email adres in');
      return;
    }
    if (!emailForm.subject) {
      toast.error('Voer een onderwerp in');
      return;
    }
    if (!emailForm.body_html) {
      toast.error('Voer een bericht in');
      return;
    }

    setSending(true);
    try {
      const payload = {
        to_email: emailForm.to_email,
        to_name: emailForm.to_name,
        subject: emailForm.subject,
        body_html: emailForm.body_html,
        customer_id: data.customer_id,
        reference_type: type?.includes('offerte') ? 'offerte' : type?.includes('factuur') ? 'factuur' : null,
        reference_id: data.reference_id,
        template_type: type
      };

      // Add PDF attachment if available
      if (pdfBase64 && pdfName) {
        payload.attachment_base64 = pdfBase64;
        payload.attachment_name = pdfName;
      }

      // Queue email instead of sending directly
      await api.post('/email-queue', payload);
      
      toast.success('Email klaargezet! Ga naar Mails om te verzenden.', {
        action: {
          label: 'Naar Mails',
          onClick: () => window.location.href = '/mails'
        }
      });
      onOpenChange(false);
      
      if (onEmailSent) {
        onEmailSent();
      }
    } catch (error) {
      console.error('Error queuing email:', error);
      toast.error(error.response?.data?.detail || 'Kon email niet klaarzetten');
    } finally {
      setSending(false);
    }
  };

  // Convert HTML to plain text for preview/editing
  const htmlToPlainText = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Simple text to HTML converter
  const plainTextToHtml = (text) => {
    return text
      .split('\n\n')
      .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
      .join('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-composer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Verzenden
          </DialogTitle>
          <DialogDescription>
            Controleer en pas de email aan voordat u deze verzendt
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Template selector */}
            <div className="space-y-2">
              <Label>Email Template</Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={(id) => {
                  const template = templates.find(t => t.id === id);
                  setSelectedTemplate(template);
                }}
              >
                <SelectTrigger data-testid="template-selector">
                  <SelectValue placeholder="Selecteer een template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipient */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="to_name">Naam ontvanger</Label>
                <Input
                  id="to_name"
                  value={emailForm.to_name}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, to_name: e.target.value }))}
                  placeholder="Jan Jansen"
                  data-testid="email-to-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="to_email">Email adres *</Label>
                <Input
                  id="to_email"
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, to_email: e.target.value }))}
                  placeholder="klant@voorbeeld.nl"
                  required
                  data-testid="email-to-address"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Onderwerp *</Label>
              <Input
                id="subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Onderwerp van de email"
                required
                data-testid="email-subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label htmlFor="body">Bericht *</Label>
              <Textarea
                id="body"
                value={htmlToPlainText(emailForm.body_html)}
                onChange={(e) => setEmailForm(prev => ({ ...prev, body_html: plainTextToHtml(e.target.value) }))}
                placeholder="Typ hier uw bericht..."
                rows={10}
                className="font-mono text-sm"
                data-testid="email-body"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Gebruik Enter voor nieuwe alinea's
              </p>
            </div>

            {/* Attachment indicator */}
            {pdfBase64 && pdfName && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm flex-1">{pdfName}</span>
                <Badge variant="secondary">
                  <FileText className="w-3 h-3 mr-1" />
                  PDF
                </Badge>
              </div>
            )}

            {/* Warning if no email account configured */}
            {templates.length === 0 && (
              <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-600">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Geen email account geconfigureerd</p>
                  <p>Configureer eerst een email account in Instellingen om emails te kunnen verzenden.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Annuleren
          </Button>
          <Button onClick={handleSend} disabled={sending || loading} data-testid="send-email-btn">
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Klaarzetten...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Klaarzetten
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailComposer;
